import cn from 'classnames';
import { InlineSvg } from 'components/svg';
import { FC, ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: ReactNode;
  size?: ModalSize;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
  hideCloseButton?: boolean;
  closeOnBackdropClick?: boolean;
}

export const Modal: FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  size = 'md',
  children,
  footer,
  className,
  hideCloseButton = false,
  closeOnBackdropClick = true
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-open');
    }
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdropClick = () => {
    if (closeOnBackdropClick) {
      onClose();
    }
  };

  const modal = (
    <>
      <div
        className="modal fade show d-block"
        role="dialog"
        tabIndex={-1}
        aria-modal="true"
        onClick={handleBackdropClick}
      >
        <div
          className={cn('modal-dialog modal-dialog-centered', `modal-${size}`, className)}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content">
            {(title || !hideCloseButton) && (
              <div className="modal-header">
                {title && <h2 className="fw-bolder mb-0">{title}</h2>}
                {!hideCloseButton && (
                  <div className="btn btn-icon btn-sm btn-active-icon-primary cursor-pointer" onClick={onClose}>
                    <InlineSvg path="/media/icons/duotune/arrows/arr061.svg" className="svg-icon-1" />
                  </div>
                )}
              </div>
            )}
            <div className="modal-body">{children}</div>
            {footer && <div className="modal-footer">{footer}</div>}
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" />
    </>
  );

  return createPortal(modal, document.body);
};
